import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { isAxiosError } from '@/react/portainer/services/axios/utils/isAxiosError';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { TRIVY_API, VulnerabilityReportTable } from '../types';

import { queryKeys } from './queryKeys';

// Projects the reports down to the CRD's printer columns instead of returning
// them whole. includeObject=Metadata brings back the labels that identify the
// workload behind each report.
const TABLE_ACCEPT = 'application/json;as=Table;v=v1;g=meta.k8s.io';

export function useVulnerabilityReportTable<T = VulnerabilityReportTable>({
  environmentId,
  enabled = true,
  select,
}: {
  environmentId: EnvironmentId;
  enabled?: boolean;
  select?: (table: VulnerabilityReportTable) => T;
}) {
  return useQuery(
    queryKeys.vulnerabilityReportTable(environmentId),
    () => getVulnerabilityReportTable(environmentId),
    {
      // No notification: VulnerabilitiesDatatable renders the failure inline,
      // and a toast per fetch buried the page behind repeats of the same error.
      // No retry either: the reads that fail here fail because the caller
      // cannot read aquasecurity.github.io or the CRD is absent, and neither
      // resolves by asking again. The page header's reload covers the rest.
      retry: false,
      enabled: enabled && !!environmentId,
      select,
    }
  );
}

async function getVulnerabilityReportTable(
  environmentId: EnvironmentId
): Promise<VulnerabilityReportTable> {
  try {
    const { data } = await axios.get<VulnerabilityReportTable>(
      `/endpoints/${environmentId}/kubernetes/${TRIVY_API}/vulnerabilityreports`,
      {
        headers: { Accept: TABLE_ACCEPT },
        params: { includeObject: 'Metadata' },
      }
    );
    return data;
  } catch (error) {
    // parseAxiosError drops the status, and the page needs it to tell a denied
    // read apart from a broken one.
    throw Object.assign(parseAxiosError(error), {
      status: isAxiosError(error) ? error.response?.status : undefined,
    });
  }
}

// isForbiddenError reports whether a rejected report read was a 403, meaning the
// caller cannot read aquasecurity.github.io rather than that anything is wrong.
export function isForbiddenError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'status' in error &&
    error.status === 403
  );
}
